/* ========================================================================
   ヘッダー共通スクリプト
   sticky-header と検索開状態の判定を管理する。
   ======================================================================== */
(() => {
  function isVisibleElement(element) {
    return (
      element instanceof HTMLElement &&
      element.offsetParent !== null &&
      element.getClientRects().length > 0
    );
  }

  class BoostMobileOverlayController {
    constructor() {
      this.mobileOverlaySelectors = [
        '.boost-sd__instant-search-container-mobile',
        '.boost-sd__search-bar-wrapper--mobile',
      ];
      this.mobileOverlayHiddenAttribute = 'data-aureme-boost-mobile-overlay-hidden';
      /*
        この cleanup は幅で分岐せず、Boost が close 直後に残したモバイルオーバーレイがあれば隠す。
        開いている間は restoreMobileOverlay() で元の inline style に戻す。
        外部 DOM の再生成や controller 再接続後も復元できるよう、退避値は data 属性に残す。
      */
      this.mobileOverlayStyleProperties = [
        {
          cssName: 'display',
          valueAttribute: 'data-aureme-boost-mobile-overlay-display',
          priorityAttribute: 'data-aureme-boost-mobile-overlay-display-priority',
          hiddenValue: 'none',
        },
        {
          cssName: 'pointer-events',
          valueAttribute: 'data-aureme-boost-mobile-overlay-pointer-events',
          priorityAttribute: 'data-aureme-boost-mobile-overlay-pointer-events-priority',
          hiddenValue: 'none',
        },
      ];
      this.mobileOverlayObserver = null;
      this.mobileOverlayObserverTimeoutId = null;
      this.mobileOverlayCleanupAnimationFrameId = null;
    }

    hideMobileOverlay() {
      this.observeMobileOverlayCleanup();

      this.hideMobileOverlayOnce();

      this.scheduleMobileOverlayCleanup();
    }

    hideMobileOverlayOnce() {
      for (const element of this.getMobileOverlayElements()) {
        this.hideMobileOverlayElement(element);
      }
    }

    hideMobileOverlayElement(element) {
      if (this.isMobileOverlayHidden(element)) return;

      if (element.getAttribute(this.mobileOverlayHiddenAttribute) !== 'true') {
        for (const styleProperty of this.mobileOverlayStyleProperties) {
          this.backupInlineStyleProperty(element, styleProperty);
        }
      }

      for (const styleProperty of this.mobileOverlayStyleProperties) {
        element.style.setProperty(styleProperty.cssName, styleProperty.hiddenValue, 'important');
      }
      element.setAttribute(this.mobileOverlayHiddenAttribute, 'true');
    }

    scheduleMobileOverlayCleanup() {
      if (typeof requestAnimationFrame !== 'function') {
        this.hideMobileOverlayOnce();
        return;
      }

      if (this.mobileOverlayCleanupAnimationFrameId !== null) return;

      this.mobileOverlayCleanupAnimationFrameId = requestAnimationFrame(() => {
        this.mobileOverlayCleanupAnimationFrameId = null;
        this.hideMobileOverlayOnce();
      });
    }

    isMobileOverlayHidden(element) {
      /*
        hidden 属性だけでなく inline style の値と priority まで確認し、
        Boost が style を書き戻した要素は再度隠せるようにする。
      */
      return (
        element.getAttribute(this.mobileOverlayHiddenAttribute) === 'true' &&
        this.mobileOverlayStyleProperties.every(
          (styleProperty) =>
            element.style.getPropertyValue(styleProperty.cssName) === styleProperty.hiddenValue &&
            element.style.getPropertyPriority(styleProperty.cssName) === 'important',
        )
      );
    }

    restoreMobileOverlay() {
      this.disconnectMobileOverlayObserver();

      for (const element of this.getMobileOverlayElements()) {
        if (element.getAttribute(this.mobileOverlayHiddenAttribute) !== 'true') continue;

        for (const styleProperty of this.mobileOverlayStyleProperties) {
          this.restoreInlineStyleProperty(element, styleProperty);
        }
        element.removeAttribute(this.mobileOverlayHiddenAttribute);
      }
    }

    observeMobileOverlayCleanup() {
      this.disconnectMobileOverlayObserver();

      if (typeof MutationObserver !== 'function' || !document.body) return;

      /* Boost 側が close 直後に style を再付与する場合があるため、短時間だけ残留オーバーレイを追従して隠す。 */
      this.mobileOverlayObserver = new MutationObserver(() => this.scheduleMobileOverlayCleanup());
      this.mobileOverlayObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
      this.mobileOverlayObserverTimeoutId = window.setTimeout(
        () => this.disconnectMobileOverlayObserver(),
        1000,
      );
    }

    disconnectMobileOverlayObserver() {
      if (this.mobileOverlayObserver) {
        this.mobileOverlayObserver.disconnect();
        this.mobileOverlayObserver = null;
      }

      if (this.mobileOverlayObserverTimeoutId) {
        window.clearTimeout(this.mobileOverlayObserverTimeoutId);
        this.mobileOverlayObserverTimeoutId = null;
      }

      if (this.mobileOverlayCleanupAnimationFrameId !== null) {
        if (typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(this.mobileOverlayCleanupAnimationFrameId);
        }

        this.mobileOverlayCleanupAnimationFrameId = null;
      }
    }

    getMobileOverlayElements() {
      return this.mobileOverlaySelectors.flatMap((selector) =>
        Array.from(document.querySelectorAll(selector)).filter(
          (element) => element instanceof HTMLElement,
        ),
      );
    }

    backupInlineStyleProperty(element, styleProperty) {
      element.setAttribute(
        styleProperty.valueAttribute,
        element.style.getPropertyValue(styleProperty.cssName),
      );
      element.setAttribute(
        styleProperty.priorityAttribute,
        element.style.getPropertyPriority(styleProperty.cssName),
      );
    }

    restoreInlineStyleProperty(element, styleProperty) {
      const value = element.getAttribute(styleProperty.valueAttribute) || '';
      const priority = element.getAttribute(styleProperty.priorityAttribute) || '';

      if (value) {
        element.style.setProperty(styleProperty.cssName, value, priority);
      } else {
        element.style.removeProperty(styleProperty.cssName);
      }

      element.removeAttribute(styleProperty.valueAttribute);
      element.removeAttribute(styleProperty.priorityAttribute);
    }
  }

  class BoostInstantSearchAdapter {
    constructor() {
      this.openElementSelectors = [
        '.boost-sd__header-item',
        '.boost-sd__suggestion-queries-link',
        '.boost-sd__suggestion-queries-item-title',
        '.boost-sd__view-all-text',
        '.boost-sd__search-form-title',
      ];
      this.bodyLockClasses = ['boost-sd__g-no-scroll', 'boost-sd__search-opening'];
      this.mobileOverlayController = new BoostMobileOverlayController();
      /* 検索 input を基準にした近傍判定の許容 px。候補リストが下へ伸びるため下方向だけ広く取る。 */
      this.marginAbove = 16;
      this.marginBelow = 320;
      this.horizontalMargin = 80;
    }

    isOpenNearHeaderInputs(visibleHeaderSearchInputs) {
      /* Boost の Instant Search は外部アプリの独自 DOM なので、検索欄近くの候補だけを開状態として扱う */
      return this.openElementSelectors.some((selector) =>
        Array.from(document.querySelectorAll(selector)).some((element) => {
          if (!isVisibleElement(element)) return false;

          const elementRect = element.getBoundingClientRect();
          return visibleHeaderSearchInputs.some((input) =>
            this.isRelatedToHeaderSearchInput(elementRect, input),
          );
        }),
      );
    }

    clearBodyLocks() {
      document.body.classList.remove(...this.bodyLockClasses);
    }

    hasBodyLocks() {
      return this.bodyLockClasses.some((className) => document.body.classList.contains(className));
    }

    hideMobileOverlay() {
      this.mobileOverlayController.hideMobileOverlay();
    }

    restoreMobileOverlay() {
      this.mobileOverlayController.restoreMobileOverlay();
    }

    disconnectMobileOverlayObserver() {
      this.mobileOverlayController.disconnectMobileOverlayObserver();
    }

    isRelatedToHeaderSearchInput(elementRect, input) {
      const inputRect = input.getBoundingClientRect();
      const isVerticallyRelated =
        elementRect.bottom >= inputRect.top - this.marginAbove &&
        elementRect.top <= inputRect.bottom + this.marginBelow;
      const isHorizontallyRelated =
        elementRect.right >= inputRect.left - this.horizontalMargin &&
        elementRect.left <= inputRect.right + this.horizontalMargin;

      return isVerticallyRelated && isHorizontallyRelated;
    }
  }

  class HeaderSearchVisibilityGuard {
    constructor(header, boostInstantSearchAdapter) {
      this.header = header;
      this.boostInstantSearchAdapter = boostInstantSearchAdapter;
    }

    isOpen() {
      if (this.hasOpenPredictiveSearch()) {
        return true;
      }

      const visibleHeaderSearchInputs = this.getVisibleHeaderSearchInputs();

      if (!visibleHeaderSearchInputs.length) {
        return false;
      }

      return this.boostInstantSearchAdapter.isOpenNearHeaderInputs(visibleHeaderSearchInputs);
    }

    hasOpenPredictiveSearch() {
      /*
        Dawn の predictive-search は open 前に loading 属性だけ立つため、
        入力直後の検索中も sticky header の自動 hide 対象から外す。
      */
      return Array.from(this.header.querySelectorAll('predictive-search')).some(
        (predictiveSearch) =>
          predictiveSearch.isOpen ||
          predictiveSearch.hasAttribute('open') ||
          predictiveSearch.hasAttribute('loading'),
      );
    }

    getVisibleHeaderSearchInputs() {
      return Array.from(
        this.header.querySelectorAll('.header__search input[type="search"]'),
      ).filter((input) => input instanceof HTMLInputElement && isVisibleElement(input));
    }
  }

  class HeaderSearchStateController {
    constructor(header, boostInstantSearchAdapter) {
      this.header = header;
      this.boostInstantSearchAdapter = boostInstantSearchAdapter;
      this.headerSection = header.headerSection || header.closest('.section-header');
      this.headerElement = header.querySelector('.header');
      this.detailsElements = [];
      this.openStateByDetails = new WeakMap();
      this.handleToggle = this.handleToggle.bind(this);
      this.handleWindowBlur = this.handleWindowBlur.bind(this);
    }

    connect() {
      this.detailsElements = Array.from(
        this.header.querySelectorAll('.header__search-mobile details'),
      ).filter((details) => details instanceof HTMLDetailsElement);

      for (const details of this.detailsElements) {
        this.openStateByDetails.set(details, details.hasAttribute('open'));
        details.addEventListener('toggle', this.handleToggle);
      }

      window.addEventListener('blur', this.handleWindowBlur);

      /* details-modal の upgrade 前に開いた状態でも、AUREME ヘッダー側の class を初期同期する */
      this.syncHeaderSearchState();
    }

    handleToggle(event) {
      const details = event.currentTarget;
      if (!(details instanceof HTMLDetailsElement)) return;

      const isOpen = details.hasAttribute('open');
      const wasOpen = this.openStateByDetails.get(details) === true;
      this.syncHeaderSearchState();

      if (isOpen && !wasOpen) {
        this.boostInstantSearchAdapter.restoreMobileOverlay();
      }

      if (wasOpen && !isOpen) {
        this.boostInstantSearchAdapter.clearBodyLocks();
        this.boostInstantSearchAdapter.hideMobileOverlay();
      }

      this.openStateByDetails.set(details, isOpen);
    }

    syncHeaderSearchState() {
      if (!this.headerSection || !this.headerElement) return;

      const isOpen = this.detailsElements.some((details) => details.hasAttribute('open'));
      this.headerSection.classList.toggle('search-open', isOpen);
      this.headerElement.classList.toggle('header-search-open', isOpen);
    }

    handleWindowBlur() {
      /* blur 直後は document.activeElement が未更新のため、次 frame で判定する */
      requestAnimationFrame(() => this.restoreScrollAfterIframeFocus());
    }

    restoreScrollAfterIframeFocus() {
      const activeElement = document.activeElement;

      if (
        !(activeElement instanceof HTMLIFrameElement) ||
        this.headerSection.contains(activeElement)
      ) {
        return;
      }
      const isSearchModalOpen = this.hasOpenSearchModal();
      const hasBoostBodyLocks = this.boostInstantSearchAdapter?.hasBodyLocks() === true;

      if (!isSearchModalOpen && !hasBoostBodyLocks) return;

      /* iframe 内の click は親 document に届かないため、検索外クリックと同じ body lock 解除を補完する */
      if (isSearchModalOpen) this.closeSearchModal();
      if (hasBoostBodyLocks) this.boostInstantSearchAdapter?.clearBodyLocks();
    }

    getSearchModals() {
      this.searchModals = this.searchModals || this.headerSection.querySelectorAll('details-modal');

      return this.searchModals;
    }

    hasOpenSearchModal() {
      return Array.from(this.getSearchModals()).some((searchModal) =>
        searchModal.querySelector('details')?.hasAttribute('open'),
      );
    }

    closeSearchModal() {
      for (const searchModal of this.getSearchModals()) {
        if (typeof searchModal.close === 'function') {
          searchModal.close(false);
        }
      }
    }

    disconnect() {
      window.removeEventListener('blur', this.handleWindowBlur);

      for (const details of this.detailsElements) {
        details.removeEventListener('toggle', this.handleToggle);
      }

      this.detailsElements = [];
      this.openStateByDetails = new WeakMap();
      this.searchModals = null;
      this.boostInstantSearchAdapter.disconnectMobileOverlayObserver();
    }
  }

  class StickyHeaderMetricsController {
    constructor(stickyHeader) {
      /* 既存の公開状態は stickyHeader に残し、計測処理側では header 名で参照する */
      this.stickyHeader = stickyHeader;
      this.header = stickyHeader;
      this.headerSection = stickyHeader.headerSection;
      this.headerBounds = stickyHeader.headerBounds || {};
      this.lastHeaderHeight = stickyHeader.lastHeaderHeight || null;

      this.update = this.update.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handleFontsReady = this.handleFontsReady.bind(this);

      this.syncPublicState();
    }

    connect() {
      this.update();
      requestAnimationFrame(() => this.update());

      window.addEventListener('load', this.update);
      window.addEventListener('pageshow', this.handlePageShow);
      this.connectFontObservers();
      this.connectResizeObserver();
      this.connectIntersectionObserver();
    }

    connectFontObservers() {
      if (!document.fonts) return;

      if (document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(this.handleFontsReady);
      }

      if (typeof document.fonts.addEventListener === 'function') {
        document.fonts.addEventListener('loadingdone', this.handleFontsReady);
        this.hasFontLoadingDoneListener = true;
      }
    }

    connectResizeObserver() {
      if (typeof ResizeObserver !== 'function') return;

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.header);
    }

    isMenuTransitioning() {
      const headerSection = this.headerSection;
      if (!headerSection) return false;

      return (
        headerSection.classList.contains('menu-open') ||
        headerSection.classList.contains('menu-closing') ||
        headerSection.classList.contains('menu-transition-lock')
      );
    }

    isRevealHideTransitioning() {
      const headerSection = this.headerSection;
      if (!headerSection) return false;

      /* class は transition 完了後も残るため、実際に走っている animation の有無まで確認する */

      return (
        (headerSection.classList.contains('shopify-section-header-hidden') ||
          headerSection.classList.contains('animate')) &&
        typeof headerSection.getAnimations === 'function' &&
        headerSection
          .getAnimations()
          .some(
            (animation) => animation.playState === 'running' || animation.playState === 'pending',
          )
      );
    }

    update(force = false) {
      if (!this.header) return;

      /* メニュー開閉中やヘッダー出入り中は height が中間値へ揺れるため、CSS 変数へ反映しない */
      const isTransitioning = this.isMenuTransitioning() || this.isRevealHideTransitioning();

      if (force || !isTransitioning) {
        const nextHeaderHeight = Math.round(this.header.getBoundingClientRect().height);
        if (nextHeaderHeight > 0 && this.lastHeaderHeight !== nextHeaderHeight) {
          document.documentElement.style.setProperty('--header-height', `${nextHeaderHeight}px`);
          this.lastHeaderHeight = nextHeaderHeight;
        }
      }

      this.updateBounds();
    }

    updateBounds() {
      if (!this.headerSection) return;

      const headerRect = this.headerSection.getBoundingClientRect();
      this.headerBounds = {
        top: Math.max(headerRect.top, 0),
        bottom: Math.max(headerRect.bottom, 0),
      };
      this.syncPublicState();
    }

    connectIntersectionObserver() {
      this.intersectionObserver = new IntersectionObserver((entries, currentObserver) => {
        this.headerBounds = entries[0].intersectionRect;
        this.syncPublicState();
        currentObserver.disconnect();
      });

      this.intersectionObserver.observe(this.headerSection);
    }

    handleFontsReady() {
      requestAnimationFrame(() => this.update());
    }

    handlePageShow() {
      requestAnimationFrame(() => this.update());
    }

    syncPublicState() {
      this.stickyHeader.headerBounds = this.headerBounds;
      this.stickyHeader.lastHeaderHeight = this.lastHeaderHeight;
    }

    disconnect() {
      window.removeEventListener('load', this.update);
      window.removeEventListener('pageshow', this.handlePageShow);

      if (document.fonts && this.hasFontLoadingDoneListener) {
        document.fonts.removeEventListener('loadingdone', this.handleFontsReady);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }
  }

  if (!customElements.get('sticky-header')) {
    class StickyHeader extends HTMLElement {
      connectedCallback() {
        this.header = this;
        this.headerSection = this.closest('.section-header');
        if (!this.headerSection) return;

        this.headerIsAlwaysSticky =
          this.getAttribute('data-sticky-type') === 'always' ||
          this.getAttribute('data-sticky-type') === 'reduce-logo-size';
        this.headerBounds = {};
        this.lastHeaderHeight = null;

        this.boostInstantSearchAdapter = new BoostInstantSearchAdapter();
        this.metricsController = new StickyHeaderMetricsController(this);
        this.searchStateController = new HeaderSearchStateController(
          this,
          this.boostInstantSearchAdapter,
        );
        this.searchVisibilityGuard = new HeaderSearchVisibilityGuard(
          this,
          this.boostInstantSearchAdapter,
        );
        this.metricsController.connect();
        this.searchStateController.connect();

        this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
        this.onMediaQueryChange = this.handleMediaQueryChange.bind(this);
        this.mobileMediaQuery.addEventListener('change', this.onMediaQueryChange);

        if (this.headerIsAlwaysSticky) {
          this.headerSection.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      }

      updateHeaderMetrics(force = false) {
        this.metricsController?.update(force);
      }

      disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);

        this.metricsController?.disconnect();
        this.metricsController = null;
        this.searchStateController?.disconnect();
        this.searchStateController = null;
        this.searchVisibilityGuard = null;
        this.boostInstantSearchAdapter = null;

        if (this.mobileMediaQuery && this.onMediaQueryChange) {
          this.mobileMediaQuery.removeEventListener('change', this.onMediaQueryChange);
        }
      }

      handleMediaQueryChange() {
        const headerDrawer = this.querySelector('header-drawer');
        const isDrawerOpen =
          headerDrawer?.mainDetailsToggle?.hasAttribute('open') &&
          !headerDrawer.mainDetailsToggle.classList.contains('menu-closing');

        /* breakpoint を跨いだときはドロワーを閉じ、desktop 側へ状態を持ち越さない */
        if (isDrawerOpen) {
          this.updateHeaderMetrics(true);
          headerDrawer.closeMenuDrawer();
        }

        this.updateHeaderMetrics();
        this.closeSearchModal();
        this.closePredictiveSearch();
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.isAnySearchUIOpen()) return;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.headerSection.classList.add('scrolled-past-header');
          if (this.preventHide) return;
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.headerSection.classList.add('scrolled-past-header');
          if (!this.preventReveal) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.headerSection.classList.remove('scrolled-past-header');
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      isAnySearchUIOpen() {
        return this.searchVisibilityGuard?.isOpen() === true;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;
        this.headerSection.classList.add(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
        );
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.headerSection.classList.add('shopify-section-header-sticky', 'animate');
        this.headerSection.classList.remove('shopify-section-header-hidden');
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        this.headerSection.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'animate',
        );
      }

      closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.headerSection.querySelectorAll('header-menu');

        for (const disclosure of this.disclosures) {
          disclosure.close();
        }
      }

      closeSearchModal() {
        this.searchStateController?.closeSearchModal();
      }

      closePredictiveSearch() {
        this.predictiveSearches =
          this.predictiveSearches || this.querySelectorAll('predictive-search');

        for (const predictiveSearch of this.predictiveSearches) {
          if (typeof predictiveSearch.close === 'function') {
            predictiveSearch.close(false);
          }
        }
      }
    }

    customElements.define('sticky-header', StickyHeader);
  }
})();
